import * as React from 'react';
import { type ApiAccountDetail, formatHeadcount, formatRevenue } from '../../lib/accounts.js';
import { CopyButton } from '../entity-drawer/copy-button.js';
import {
  type DetailField,
  DetailFieldGrid,
  PropertySection,
} from '../entity-drawer/entity-drawer-sections.js';
import { CitedValue } from '../entity-drawer/field-evidence-link.js';

// What the firm says it is, from its own site; values carry a source arrow, except phone and email, which copy.

const SiteMap = React.lazy(() => import('../projects/site-map.js'));

export type AccountProfileFacts = Pick<
  ApiAccountDetail,
  | 'field_evidence'
  | 'hq_address'
  | 'hq_location'
  | 'public_phone'
  | 'public_email'
  | 'specialties'
  | 'description'
  | 'founded_year'
  | 'firmographics'
>;

/** The firm's own description, linked to the page it came from. */
export function AccountAbout({ account }: { account: AccountProfileFacts }) {
  if (!account.description) return null;
  return (
    <section className="space-y-2 text-sm">
      <h3 className="font-semibold text-foreground">About</h3>
      <p className="leading-6 text-foreground">
        <CitedValue evidence={account.field_evidence?.description}>
          {account.description}
        </CitedValue>
      </p>
    </section>
  );
}

/** Headquarters, phone, mailbox and founding year, with the office on a small map. */
export function AccountCompanySection({
  account,
  mapboxToken,
  theme,
  columns = 2,
}: {
  account: AccountProfileFacts;
  mapboxToken: string | undefined;
  theme: 'light' | 'dark';
  columns?: 1 | 2;
}) {
  const evidence = account.field_evidence ?? {};
  const fields: DetailField[] = [];
  if (account.hq_address)
    fields.push({
      label: 'Headquarters',
      value: <CitedValue evidence={evidence.hq_address}>{account.hq_address}</CitedValue>,
    });
  if (account.public_phone)
    fields.push({
      label: 'Phone',
      value: (
        <span className="inline-flex items-center gap-1.5">
          {account.public_phone}
          <CopyButton value={account.public_phone} label="Copy phone" />
        </span>
      ),
    });
  if (account.public_email)
    fields.push({
      label: 'Email',
      value: (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {account.public_email}
          <CopyButton value={account.public_email} label="Copy email" />
        </span>
      ),
    });
  const firm = account.firmographics ?? null;
  const employees = formatHeadcount(firm);
  if (employees) {
    const growth = firm?.headcount_growth_1y;
    fields.push({
      label: 'Employees',
      value:
        growth != null ? (
          <span>
            {employees}
            <span className="ml-1.5 text-xs text-muted-foreground">
              {`${growth > 0 ? '+' : ''}${growth}% in a year`}
            </span>
          </span>
        ) : (
          employees
        ),
    });
  }
  if (firm?.industry) fields.push({ label: 'Industry', value: firm.industry });
  if (firm?.company_type) fields.push({ label: 'Company type', value: firm.company_type });
  const revenue = formatRevenue(firm);
  if (revenue) fields.push({ label: 'Revenue', value: revenue });
  if (account.founded_year)
    fields.push({
      label: 'Founded',
      value: (
        <CitedValue evidence={evidence.founded_year}>{String(account.founded_year)}</CitedValue>
      ),
    });
  else if (firm?.founded_year) fields.push({ label: 'Founded', value: String(firm.founded_year) });
  const office = account.hq_location && mapboxToken ? account.hq_location : null;
  if (fields.length === 0 && !office) return null;

  return (
    <PropertySection title="Company">
      {fields.length > 0 ? <DetailFieldGrid fields={fields} columns={columns} /> : null}
      {office ? (
        <div className="h-40">
          <React.Suspense fallback={<div className="h-full rounded-md border bg-muted" />}>
            <SiteMap
              lat={office.lat}
              lng={office.lng}
              token={mapboxToken}
              theme={theme}
              caption="Office location"
              hideCaption
              imageSize={{ width: 800, height: 320 }}
            />
          </React.Suspense>
        </div>
      ) : null}
    </PropertySection>
  );
}

/** What the firm says it does. */
export function AccountSpecialtiesSection({ account }: { account: AccountProfileFacts }) {
  const specialties = account.specialties ?? [];
  if (specialties.length === 0) return null;
  return (
    <PropertySection title="Specialties">
      <ul className="flex flex-wrap gap-1.5">
        {specialties.map((specialty) => (
          <li
            key={specialty}
            data-testid="specialty"
            className="rounded-md border bg-muted/40 px-2 py-0.5 text-xs text-foreground"
          >
            {specialty}
          </li>
        ))}
      </ul>
    </PropertySection>
  );
}
